"use client";

var __jsx = React.createElement;
import React, { createContext, useContext, useEffect, useState } from "react";
import { DEFAULT_LOCALE, LOCALES } from "@lib/constants";
import { useTranslations } from "next-intl";
import { useRouter, usePathname } from "navigation";
import { setStoredLocale, isValidLocale } from "@lib/util/locale-storage";
const LanguageConfigContext = /*#__PURE__*/createContext(null);
/**
 * Enhanced LanguageConfigProvider that integrates with LocaleHandler component
 * and provides comprehensive locale management including:
 * - localStorage persistence
 * - Server-side cookie synchronization
 * - next-intl router navigation
 * - Locale validation and fallback handling
 * - Event-based synchronization with other components
 */
export const LanguageConfigProvider = _ref => {
  var _LOCALES$split$map$fi;
  let {
    locale,
    children
  } = _ref;
  const t = useTranslations("Shared.LangCurrencyConfig");
  const router = useRouter();
  const pathname = usePathname();
  const {
    0: currentLanguageCode,
    1: setCurrentLanguageCode
  } = useState(process.env.NEXT_PUBLIC_DEFAULT_LOCALE || DEFAULT_LOCALE);
  const {
    0: currentLanguage,
    1: setCurrentLanguage
  } = useState(t(currentLanguageCode));
  useEffect(() => {
    changeLanguageStates(locale);
    setCurrentLanguageCode(locale);
  }, [locale]);

  // Listen for locale changes from LocaleHandler component
  useEffect(() => {
    const handleLocaleChange = event => {
      const {
        locale: newLocale
      } = event.detail;
      if (newLocale && newLocale !== currentLanguageCode) {
        changeLanguageStates(newLocale);
        setCurrentLanguageCode(newLocale);
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('localeChange', handleLocaleChange);
      return () => {
        window.removeEventListener('localeChange', handleLocaleChange);
      };
    }
  }, [currentLanguageCode]);
  const changeLanguageStates = languageCode => {
    setCurrentLanguageCode(languageCode);
    setCurrentLanguage(t(languageCode));
  };

  /**
   * Get path parts from current URL
   * Returns an object with parsed path information
   */
  const getPathParts = () => {
    if (typeof window === 'undefined') return null;
    const pathname = window.location.pathname;
    const query = window.location.search;
    const trimmedPathname = pathname.startsWith("/") ? pathname.slice(1) : pathname;
    const pathParts = trimmedPathname.split("/");
    const hasLocale = localesList.includes(pathParts[0]);
    return {
      pathname,
      query,
      trimmedPathname,
      pathParts,
      hasLocale,
      currentLocale: hasLocale ? pathParts[0] : null,
      pathWithoutLocale: hasLocale ? pathParts.slice(1).join("/") : trimmedPathname
    };
  };

  /**
   * Changes the application locale with comprehensive synchronization
   * - Updates localStorage for client-side persistence
   * - Sets secure cookies for server-side consistency
   * - Updates DOM attributes for accessibility
   * - Constructs proper URL with path parts
   * - Validates input and provides fallback handling
   */
  const changePageLocale = newLocale => {
    // Validate locale using utility function
    if (!isValidLocale(newLocale)) {
      console.warn("Invalid locale provided: ".concat(newLocale, ", using default: ").concat(DEFAULT_LOCALE));
      newLocale = DEFAULT_LOCALE;
    }

    // Get path parts for URL construction
    const pathInfo = getPathParts();
    console.log('Path parts:', pathInfo);
    if (!pathInfo) {
      console.warn('Could not get path information, using fallback');
      return;
    }
    try {
      // Set locale in localStorage and cookies using utility function
      const success = setStoredLocale(newLocale);
      if (success) {
        // Update HTML lang attribute for accessibility
        document.documentElement.lang = newLocale;

        // Update internal state immediately
        changeLanguageStates(newLocale);

        // Construct new URL with path parts
        let newUrl = "";
        if (pathInfo.hasLocale) {
          // Replace existing locale with new one
          const newPathParts = [...pathInfo.pathParts];
          newPathParts[0] = newLocale;
          newUrl = "/" + newPathParts.join("/");
        } else {
          // Add locale prefix to existing path
          newUrl = "/" + newLocale + "/" + pathInfo.trimmedPathname;
        }

        // Add query string if it exists
        newUrl += pathInfo.query;
        console.log('Navigating to:', newUrl);

        // Use the constructed newUrl for navigation
        console.log('Using constructed URL:', newUrl);
        window.history.replaceState({}, "", newUrl);
        window.location.replace(newUrl);
        console.log("Locale changed successfully: ".concat(newLocale));
      } else {
        throw new Error('Failed to set locale in storage');
      }
    } catch (error) {
      // Handle localStorage errors gracefully (e.g., in private browsing)
      console.warn('Failed to change locale:', error);
      // Fallback to page reload if all else fails
      window.location.reload();
    }
  };
  const getLanguageOptions = () => {
    const options = localesList.map(locale => {
      return {
        key: locale,
        value: t(locale)
      };
    });
    return options;
  };
  const localesList = (_LOCALES$split$map$fi = LOCALES === null || LOCALES === void 0 ? void 0 : LOCALES.split(",").map(s => s.trim()).filter(Boolean)) !== null && _LOCALES$split$map$fi !== void 0 ? _LOCALES$split$map$fi : [DEFAULT_LOCALE];

  // Helper functions for locale validation (using utility functions)
  const isValidLocaleHelper = locale => {
    return isValidLocale(locale);
  };
  const getSupportedLocales = () => {
    return localesList;
  };
  return __jsx(LanguageConfigContext.Provider, {
    value: {
      currentLanguageCode,
      currentLanguage,
      getLanguageOptions,
      changePageLocale,
      changeLanguageStates,
      isValidLocale: isValidLocaleHelper,
      getSupportedLocales,
      getPathParts
    }
  }, children);
};
export const useLanguageConfig = () => {
  const context = useContext(LanguageConfigContext);
  if (context === null) {
    throw new Error("useLanguageConfig must be used within a LanguageConfigProvider");
  }
  return context;
};